import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PassStatus } from '@sss/types';
import { PASS_PATTERN, getPassStatus, relayProblem, type RelayProblem } from '../../lib/relayApi';
import { formatBytes } from '../../lib/tools';
import ui from '../../styles/ui.module.css';
import styles from '../../pages/LivePage.module.css';

interface Props {
  /** The pass kept in this browser, if any. */
  pass: string | null;
  onChange(pass: string | null): void;
  /** Bytes the chosen files need. */
  needed: number;
  /** Translation keys to use instead of the relay's wording (the drop form, docs/adr/0015). */
  hintKey?: string;
  notEnoughKey?: string;
  haveKey?: string;
}

/**
 * Files over 25 MB: add a relay pass, or see what the one in this browser
 * has left (docs/adr/0013). The pass is a bearer token, so the field is a
 * password input and the token is never shown again once added.
 */
export default function RelayPassField({
  pass,
  onChange,
  needed,
  hintKey = 'live.relayPass.hint',
  notEnoughKey = 'live.relayPass.notEnough',
  haveKey = 'live.relayPass.have',
}: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [status, setStatus] = useState<PassStatus | null>(null);
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<RelayProblem | 'invalid' | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!pass) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    getPassStatus(pass)
      .then((s) => !cancelled && setStatus(s))
      .catch((err: unknown) => {
        if (cancelled) return;
        const found = relayProblem(err);
        // A pass that's gone or used up isn't worth keeping; anything else may be passing.
        if (found === 'passNotFound' || found === 'passExpired') onChange(null);
        setProblem(found);
      });
    return () => {
      cancelled = true;
    };
  }, [pass, onChange]);

  async function add() {
    const candidate = draft.trim();
    if (!PASS_PATTERN.test(candidate)) {
      setProblem('invalid');
      return;
    }
    setChecking(true);
    setProblem(null);
    try {
      setStatus(await getPassStatus(candidate));
      setDraft('');
      onChange(candidate);
    } catch (err) {
      setProblem(relayProblem(err));
    } finally {
      setChecking(false);
    }
  }

  const problemLine = problem && (
    <span role="alert" className={`${ui.error} ${styles.hintNote}`}>
      {t(`live.relayPass.problems.${problem}`)}
    </span>
  );

  if (pass) {
    const date = status ? new Date(status.expiresAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }) : '';
    return (
      <div className={styles.relayPass} data-testid="relay-pass">
        <p className={ui.hint} aria-live="polite">
          {status ? t(haveKey, { left: formatBytes(status.bytesLeft), date }) : t('live.relayPass.checking')}
          {status && status.bytesLeft < needed && <span className={styles.hintNote}>{t(notEnoughKey, { left: formatBytes(status.bytesLeft), needed: formatBytes(needed) })}</span>}
          {problemLine}
        </p>
        <button
          type="button"
          className={ui.buttonSecondary}
          onClick={() => {
            setProblem(null);
            onChange(null);
          }}
        >
          {t('live.relayPass.remove')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.relayPass}>
      <label className={ui.field} htmlFor={id}>
        {t('live.relayPass.label')}
      </label>
      <div className={styles.relayPassRow}>
        <input
          id={id}
          type="password"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter would submit the whole form and start the handoff.
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="sss_pass_…"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={`${id}-hint`}
          aria-invalid={problem === 'invalid' || problem === 'passNotFound' || undefined}
        />
        <button type="button" className={ui.buttonSecondary} onClick={() => void add()} disabled={checking}>
          {checking ? t('live.relayPass.adding') : t('live.relayPass.add')}
        </button>
      </div>
      <p id={`${id}-hint`} className={ui.hint}>
        {t(hintKey)}{' '}
        {/* Its own tab, so this form and anything typed into it survive the payment. */}
        <a href="/pass/buy" target="_blank" rel="noopener">
          {t('live.relayPass.buy')}
        </a>
        {problemLine}
      </p>
    </div>
  );
}
